import React, { useState, useEffect } from 'react';
import { del } from 'aws-amplify/api';
import { FiUser, FiUsers, FiTrendingUp, FiClock, FiMessageSquare, FiTrash2 } from 'react-icons/fi';
import { sessionCache, CachedSession } from '../../utils/sessionCache';

type Session = CachedSession;

type SessionManagerProps = {
  onSelectSession: (sessionId: string, persona: string, isNewSession?: boolean) => void;
  currentSessionId?: string;
  onSessionListRefresh?: (refreshCallback: () => void) => void;
};

const SessionManager: React.FC<SessionManagerProps> = ({ onSelectSession, currentSessionId, onSessionListRefresh }) => {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFetching, setIsFetching] = useState(false);

  // Create refresh function and pass it to parent
  const refreshSessions = () => {
    const updatedSessions = sessionCache.getCachedSessions();
    setSessions(updatedSessions);
    console.log('Session list refreshed');
  };

  // Pass refresh callback to parent on mount
  useEffect(() => {
    if (onSessionListRefresh) {
      onSessionListRefresh(refreshSessions);
    }
  }, [onSessionListRefresh]);

  useEffect(() => {
    // Fetch sessions only from cache (no backend merging)
    const fetchSessions = async () => {
      // Prevent multiple concurrent fetches
      if (isFetching) {
        console.log('Fetch already in progress, skipping...');
        return;
      }

      setIsFetching(true);
      setLoading(true);

      try {
        // Initialize cache and load cached sessions only
        sessionCache.initialize();
        const cachedSessions = sessionCache.getCachedSessions();
        setSessions(cachedSessions);

        console.log('Loaded cached sessions:', cachedSessions.length);

        setError(null);
      } catch (err) {
        console.error('Error fetching sessions:', err);
        setError('Failed to load sessions');
      } finally {
        setLoading(false);
        setIsFetching(false);
      }
    };

    fetchSessions();
  }, [isFetching]);

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Get persona display name
  const getPersonaName = (persona: string) => {
    switch (persona) {
      case 'advisor':
        return 'Advisor';
      case 'partner':
        return 'Partner';
      case 'tam':
        return 'TAM';
      default:
        return persona;
    }
  };

  // Get persona icon
  const getPersonaIcon = (persona: string) => {
    switch (persona) {
      case 'advisor':
        return <FiUser className="h-4 w-4" />;
      case 'partner':
        return <FiUsers className="h-4 w-4" />;
      case 'tam':
        return <FiTrendingUp className="h-4 w-4" />;
      default:
        return <FiMessageSquare className="h-4 w-4" />;
    }
  };

  // Get persona badge class
  const getPersonaBadgeClass = (persona: string) => {
    switch (persona) {
      case 'advisor':
        return 'bg-primary-100 text-primary-700';
      case 'partner':
        return 'bg-secondary-100 text-secondary-700';
      case 'tam':
        return 'bg-accent-100 text-accent-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  // Create new session
  // Create new session - Option C: Sessions auto-created on first message
  const createNewSession = async (persona: string = 'advisor') => {
    try {
      // Create a temporary session that will be replaced with real sessionId on first message
      const tempSessionId = `temp-${Date.now()}`;

      console.log('Creating temporary session:', tempSessionId);

      // Create temporary session in cache (will be replaced with real sessionId)
      sessionCache.createSession(tempSessionId, persona, `New ${getPersonaName(persona)} Conversation`);

      // Update UI
      const updatedSessions = sessionCache.getCachedSessions();
      setSessions(updatedSessions);
      onSelectSession(tempSessionId, persona, true);
    } catch (err) {
      console.error('Error creating session:', err);
      setError('Failed to create new session');
    }
  };

  // Delete session - Handle both temporary and backend sessions
  const deleteSession = async (sessionId: string) => {
    try {
      console.log('Deleting session:', sessionId);

      // If it's a temporary session, just delete from cache
      if (sessionId.startsWith('temp-')) {
        console.log('Deleting temporary session (cache only):', sessionId);
        sessionCache.deleteSession(sessionId);

        // Update UI
        const updatedSessions = sessionCache.getCachedSessions();
        setSessions(updatedSessions);

        // If this was the current session, redirect to new session
        if (sessionId === currentSessionId) {
          createNewSession();
        }

        console.log('Temporary session deleted successfully:', sessionId);
        return;
      }

      // For real sessions, try to delete from backend
      try {
        const response = await del({
          apiName: 'api',
          path: '/api/sessions',
          options: {
            body: JSON.stringify({
              sessionId: sessionId,
            }),
            headers: {
              'Content-Type': 'application/json',
            },
          },
        }).response;
        const data = (await response.body.json()) as any;
        console.log('Backend deletion response:', data);
      } catch (backendError: any) {
        // If session not found in backend, that's OK - it might be a cached-only session
        if (backendError.response?.data?.error === 'SESSION_NOT_FOUND' || backendError.message?.includes('not found')) {
          console.log('Session not found in backend (likely cached-only session):', sessionId);
        } else {
          // Re-throw other errors
          throw backendError;
        }
      }

      // Always remove from cache (whether backend deletion succeeded or not)
      sessionCache.deleteSession(sessionId);

      // Update UI
      const updatedSessions = sessionCache.getCachedSessions();
      setSessions(updatedSessions);

      console.log('Session deleted successfully:', sessionId);

      // If this was the current session, redirect to new session
      if (sessionId === currentSessionId) {
        createNewSession();
      }
    } catch (err) {
      console.error('Error deleting session:', err);
      setError('Failed to delete session');
    }
  };

  // Delete all sessions - Handle both temporary and backend sessions
  const deleteAllSessions = async () => {
    if (!window.confirm('Are you sure you want to delete all conversations? This cannot be undone.')) {
      return;
    }

    try {
      console.log('Deleting all sessions');

      // Get current sessions
      const currentSessions = sessions;

      // Separate temporary and real sessions
      const tempSessions = currentSessions.filter((s) => s.sessionId.startsWith('temp-'));
      const realSessions = currentSessions.filter((s) => !s.sessionId.startsWith('temp-'));

      console.log(`Found ${tempSessions.length} temporary sessions and ${realSessions.length} real sessions`);

      // Delete real sessions from backend
      if (realSessions.length > 0) {
        const backendDeletionPromises = realSessions.map(async (session) => {
          try {
            await del({
              apiName: 'api',
              path: '/api/sessions',
              options: {
                body: JSON.stringify({ sessionId: session.sessionId }),
                headers: {
                  'Content-Type': 'application/json',
                },
              },
            }).response;
            console.log('Deleted backend session:', session.sessionId);
          } catch (error: any) {
            // Ignore "not found" errors - session was likely cached-only
            if (error.response?.data?.error === 'SESSION_NOT_FOUND' || error.message?.includes('not found')) {
              console.log('Session not in backend (cached-only):', session.sessionId);
            } else {
              console.warn('Error deleting backend session:', session.sessionId, error);
            }
          }
        });

        // Wait for all backend deletions to complete (or fail gracefully)
        await Promise.allSettled(backendDeletionPromises);
      }

      // Clear all frontend cache (both temporary and real sessions)
      sessionCache.clearAllCache();

      // Update UI
      setSessions([]);

      console.log('All sessions deleted successfully');

      // Create a new session
      createNewSession();
    } catch (err) {
      console.error('Error deleting all sessions:', err);
      setError('Failed to delete all sessions');
    }
  };

  return (
    <div className="session-manager p-4">
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-lg font-semibold">Previous Conversations</h3>
        <div className="flex gap-2">
          <button
            onClick={() => createNewSession()}
            className="px-3 py-1 bg-primary-500 text-white rounded-md hover:bg-primary-600"
          >
            New Chat
          </button>
          {sessions.length > 0 && (
            <button
              onClick={deleteAllSessions}
              className="px-3 py-1 bg-red-500 text-white rounded-md hover:bg-red-600"
              title="Delete all conversations"
            >
              Clear All
            </button>
          )}
        </div>
      </div>

      {loading && (
        <div className="loading flex justify-center py-4">
          <FiClock className="animate-spin mr-2" /> Loading sessions...
        </div>
      )}
      {error && <div className="error text-error py-2">{error}</div>}

      <div className="session-list space-y-2 mt-2">
        {sessions.length === 0 && !loading ? (
          <div className="no-sessions text-center py-4 text-secondary-500">No previous conversations found</div>
        ) : (
          sessions.map((session) => (
            <div
              key={session.sessionId}
              className={`session-item p-3 rounded-qnav ${
                currentSessionId === session.sessionId
                  ? 'bg-primary-50 border-l-4 border-primary-500'
                  : 'bg-surface hover:bg-secondary-50 border border-secondary-200'
              }`}
            >
              <div className="flex justify-between items-start">
                <div
                  className="flex-1 cursor-pointer"
                  onClick={() => onSelectSession(session.sessionId, session.persona)}
                >
                  <div className="session-info flex justify-between items-center mb-1">
                    <span className="session-date text-xs text-secondary-500">
                      <FiClock className="inline mr-1" /> {formatDate(session.createdAt)}
                    </span>
                    <span
                      className={`persona-badge text-xs px-2 py-1 rounded-full flex items-center ${getPersonaBadgeClass(session.persona)}`}
                    >
                      {getPersonaIcon(session.persona)}
                      <span className="ml-1">{getPersonaName(session.persona)}</span>
                    </span>
                  </div>
                  <div className="session-preview text-sm text-secondary-700 truncate">{session.name}</div>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (window.confirm('Delete this conversation?')) {
                      deleteSession(session.sessionId);
                    }
                  }}
                  className="ml-2 p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                  title="Delete conversation"
                >
                  <FiTrash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default SessionManager;
